import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { Box, DialogContent, Grid, Stack, styled } from '@mui/material';
import { FC, Fragment, useEffect, useState } from 'react';
import { LuBuilding2, LuMessageSquare, LuBriefcase } from 'react-icons/lu';
import { getJobById, IJob } from 'wisestep-api';
import {
  WsColors,
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  WsBadge,
  WsBadgetype,
  WsBadgeSize,
} from 'wisestep-base';

import { Page } from '../../../types/Page';
import { defaultEmptyPage } from '../../../utils/defaultPageData';
import WsAvatar from '../../teams/components/WsAvatar';
import WsLink from '../../teams/components/WsLink';
import { ICommunicationLog, SmsDetails } from '../types/CommunicationTypes';
import {
  defaultCommunicationLog,
  formatCommunicationDate,
  formatJobTitle,
  getAllCommunicationLogs,
  getCommStatusBadge,
  getMessageStatusBadgeProps,
} from '../utils/communicationUtils';

interface MessageHistoryProps {
  smsLog: ICommunicationLog<SmsDetails>;
  jobInfo?: IJob;
  isLoading?: boolean;
}

const ContactInfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: WsColors.TertiaryShades50,
  padding: theme.spacing(1.5, 3.5),
  borderRadius: theme.spacing(0.875),
  margin: theme.spacing(2, 2, 0, 2),
}));

const InfoRow = styled(Stack)(() => ({
  alignItems: 'center',
  gap: 9,
}));

const TimelineContainer = styled(Box)(() => ({
  height: 537,
  padding: '16px',
  backgroundColor: WsColors.White,
  overflowY: 'auto',
}));

const MessageBubble = styled(Box)(({ theme }) => ({
  backgroundColor: WsColors.TheBlues100,
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  border: `1px solid ${WsColors.TertiaryShades200}`,
  maxWidth: 380,
  position: 'relative',
}));

const StatusBadgeContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  marginTop: 8,
  flexWrap: 'wrap',
}));

const InfoIcon = styled(Box)(() => ({
  width: 18,
  height: 18,
  color: WsColors.TertiaryShades800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const MessageHistory: FC<MessageHistoryProps> = ({ smsLog, isLoading = false }) => {
  smsLog = smsLog ?? defaultCommunicationLog();
  const [job, setJob] = useState<IJob | null>(null);
  const [messages, setMessages] = useState<Page<ICommunicationLog>>(defaultEmptyPage);

  useEffect(() => {
    const fetchSmsConversation = async () => {
      try {
        const response = await getAllCommunicationLogs({
          recruiterId: smsLog.recruiterId,
          jobId: smsLog.jobId,
          candidateId: smsLog.candidateId,
          page: 0,
          size: 50,
          type: 'SMS',
        });
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching SMS conversation:', error);
      }
    };
    fetchSmsConversation();
  }, [smsLog.candidateId, smsLog.jobId, smsLog.recruiterId]);

  useEffect(() => {
    if (!smsLog.jobId) return;
    const fetchJobData = async () => {
      const jobData: IJob = (await getJobById(smsLog.jobId)).data;
      jobData && setJob(jobData);
    };
    fetchJobData();
  }, [smsLog.jobId]);

  const sortedMessages = [...messages.content].sort(
    (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()
  );

  return (
    <DialogContent sx={{ padding: 0, height: '100%', overflow: 'hidden' }}>
      <Grid container direction="column" sx={{ height: '100%' }}>
        <Grid item>
          <ContactInfoContainer>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 1.5 }}>
              <WsAvatar name={smsLog.candidateName} size="small" variant="circular" />
              <Box flex={1}>
                <WsTypography
                  variant={WsTypoVariants.BODY1}
                  color={WsTypoColors.PRIMARY}
                  bold
                  cursor="pointer"
                >
                  {smsLog.candidateName}
                </WsTypography>
              </Box>
              {getCommStatusBadge(smsLog.status, smsLog.type)}
            </Stack>
            <Stack direction="row" alignItems="center" gap={4}>
              <InfoRow display="flex" direction="row">
                <InfoIcon>
                  <LuBriefcase size={18} />
                </InfoIcon>
                <WsLink to={`/jobs-crm/${smsLog.jobId}/false/0/pipeline`}>
                  <WsTypography
                    cursor="pointer"
                    variant={WsTypoVariants.BODY2}
                    color={WsTypoColors.PRIMARY750}
                    bold
                  >
                    {formatJobTitle(job?.title || 'Unknown Job Title', job?.jobCode)}
                  </WsTypography>
                </WsLink>
              </InfoRow>
              <InfoRow display="flex" direction="row">
                <InfoIcon>
                  <LuBuilding2 size={18} />
                </InfoIcon>
                <WsLink
                  to={`/client-details?name=${job?.companyName}&isChild=true&clientId=${
                    job?.clientId ?? ''
                  }&jobCode=${job?.jobCode}&tabIndex=0`}
                >
                  <WsTypography
                    cursor="pointer"
                    variant={WsTypoVariants.BODY2}
                    color={WsTypoColors.PRIMARY750}
                    bold
                  >
                    {job?.companyName ?? 'Not Available'}
                  </WsTypography>
                </WsLink>
              </InfoRow>
            </Stack>
          </ContactInfoContainer>
        </Grid>
        <Grid item flex={1} sx={{ overflow: 'hidden' }}>
          <TimelineContainer>
            <Timeline
              sx={{
                padding: 0,
                '& .MuiTimelineItem-root': {
                  '&:before': {
                    content: 'none',
                  },
                  minHeight: 'auto',
                },
                '& .MuiTimelineConnector-root': {
                  backgroundColor: '#EAEAF1',
                  width: '1px',
                },
                '& .MuiTimelineSeparator-root': {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                },
                '& .MuiTimelineDot-root': {
                  margin: 0,
                  padding: 0,
                  boxShadow: 'none',
                },
              }}
            >
              {sortedMessages.map((comm, index) => {
                const smsDetails = comm.details as SmsDetails;
                const phoneNumber =
                  comm.direction === 'INBOUND' ? comm.contactFrom : comm.contactTo;
                const fromName = comm.direction === 'INBOUND' ? comm.contactFrom : undefined;
                const toName = comm.direction === 'OUTBOUND' ? comm.contactTo : undefined;

                return (
                  <Fragment key={comm.sourceId}>
                    <TimelineItem>
                      <TimelineSeparator>
                        <TimelineDot
                          sx={{
                            backgroundColor: '#00BBA4',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 'none',
                            margin: 0,
                            padding: 0,
                            boxShadow: 'none',
                            position: 'relative',
                          }}
                        >
                          <LuMessageSquare size={16} color={WsColors.White} />
                        </TimelineDot>
                        {index < sortedMessages.length - 1 && <TimelineConnector />}
                      </TimelineSeparator>
                      <TimelineContent sx={{ paddingBottom: 3 }}>
                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                          <WsTypography
                            variant={WsTypoVariants.CAPTION}
                            color={WsTypoColors.SENARY}
                          >
                            {formatCommunicationDate(comm.createdOn)}
                          </WsTypography>
                          <WsTypography
                            variant={WsTypoVariants.CAPTION}
                            color={WsTypoColors.PRIMARY}
                            bold
                          >
                            {comm.direction === 'OUTBOUND' ? 'from' : 'to'} {phoneNumber}
                          </WsTypography>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ mb: 1 }}>
                          <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.SENARY}>
                            {comm.direction === 'OUTBOUND' ? 'Message from' : 'Message to'}
                          </WsTypography>
                          <WsTypography
                            variant={WsTypoVariants.BODY1}
                            color={WsTypoColors.PRIMARY750}
                            bold
                          >
                            {comm.direction === 'OUTBOUND' ? fromName : toName}
                          </WsTypography>
                          {comm.direction === 'INBOUND' && fromName && (
                            <>
                              <WsTypography
                                variant={WsTypoVariants.BODY1}
                                color={WsTypoColors.SENARY}
                              >
                                from
                              </WsTypography>
                              <WsTypography
                                variant={WsTypoVariants.BODY1}
                                color={WsTypoColors.PRIMARY750}
                                bold
                              >
                                {fromName}
                              </WsTypography>
                            </>
                          )}
                        </Stack>
                        {smsDetails.body && (
                          <MessageBubble>
                            <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.BLACK}>
                              {smsDetails.body}
                            </WsTypography>
                          </MessageBubble>
                        )}
                        <StatusBadgeContainer>
                          <WsBadge
                            label={
                              getMessageStatusBadgeProps(
                                comm.status,
                                smsDetails.creditConsumed || 0
                              ).label
                            }
                            badgeType={
                              getMessageStatusBadgeProps(
                                comm.status,
                                smsDetails.creditConsumed || 0
                              ).badgeType
                            }
                            badgeSize={WsBadgeSize.MEDIUM}
                          />
                          {comm.status === 'SENT' && (
                            <WsBadge
                              label={`Credits: ${smsDetails.creditConsumed || 0}`}
                              badgeType={WsBadgetype.WARNING}
                              badgeSize={WsBadgeSize.MEDIUM}
                            />
                          )}
                        </StatusBadgeContainer>
                      </TimelineContent>
                    </TimelineItem>
                    {index < sortedMessages.length - 1 && (
                      <TimelineItem
                        sx={{
                          minHeight: 24,
                          '& .MuiTimelineContent-root': {
                            display: 'none',
                          },
                        }}
                      >
                        <TimelineSeparator>
                          <TimelineConnector />
                        </TimelineSeparator>
                        <TimelineContent />
                      </TimelineItem>
                    )}
                  </Fragment>
                );
              })}
            </Timeline>
            {isLoading && (
              <Box display="flex" justifyContent="center" mt={2}>
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.QUATERNARY}>
                  Loading message history...
                </WsTypography>
              </Box>
            )}
            {sortedMessages.length === 0 && !isLoading && (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <WsTypography variant={WsTypoVariants.BODY1} color={WsTypoColors.QUATERNARY}>
                  No message history available
                </WsTypography>
              </Box>
            )}
          </TimelineContainer>
        </Grid>
      </Grid>
    </DialogContent>
  );
};

export default MessageHistory;
